import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { Box, Stack } from "@mui/material";
import {
  InvestigationAdvertisement,
  InvestigationSectionHeader,
  Separator,
} from "../components";
import { patchInvestigation } from "../../../../../apis";
import { serializeDayForAPI } from "../../../../../helpers/dateTransform";
import { EGButton } from "../../../../MainComponents/Button/EGButton/EGButton";
import { useConstants } from "../Main/useConstants";
import { AddConclusion } from "./AddConclusion";
import { SingleConclusion } from "./SingleConclusion";
import { Conclude } from "./Modals/Conclude";
import _ from "lodash";

export const Conclusion = ({
  investigation,
  setInvestigation,
  justView = false,
  handleSelectedSection,
  onReload,
}) => {
  const [t] = useTranslation("investigation");
  const { enums } = useConstants();

  const [conclusion, setConclusion] = useState(() => {
    if (investigation.conclusion === null) return null;
    return {
      conclusion: investigation.conclusion,
      conclusion_at: investigation.conclusion_at,
      comment: investigation.comment ?? "",
      files: investigation.conclusion_files ?? [],
      measures: investigation.measures ?? [],
    };
  });

  const [isEditing, setIsEditing] = useState(false);
  const [showConclude, setShowConclude] = useState(false);
  const [isLoading, setIsLoading] = useState(false);

  const someConclusionExist = investigation.conclusion !== null;

  useEffect(() => {
    if (investigation.conclusion !== null) {
      setConclusion({
        conclusion: investigation.conclusion,
        conclusion_at: investigation.conclusion_at,
        comment: investigation.comment ?? "",
        files: investigation.conclusion_files ?? [],
        measures: investigation.measures ?? [],
      });
    }
  }, [investigation]);

  const handleSaveConclusionDetails = async (newConclusions) => {
    setIsLoading(true);
    const objToSave = _.cloneDeep(newConclusions);
    try {
      await patchInvestigation(investigation.alias, objToSave);
      setInvestigation((prev) => ({ ...prev, ...objToSave }));
      onReload();
    } catch (error) {
      console.error(error);
      throw error;
    } finally {
      setIsLoading(false);
    }
  };

  // Concluir desde el botón rojo: guarda la conclusión existente con la fecha elegida
  const handleConcludeInvestigation = async (conclusionAt) => {
    await handleSaveConclusionDetails({
      conclusion: conclusion.conclusion ?? "",
      comment: conclusion.comment ?? "",
      conclusion_at: serializeDayForAPI(conclusionAt),
    });
  };

  // 🎯 Funciones auxiliares para renderizar
  const renderHeader = () =>
    !isEditing &&
    !justView && (
      <InvestigationSectionHeader
        text={t("Conclusion.add_conclusion_to_investigation")}
        buttonText={t("Conclusion.add_conclusion_to_investigation_btn")}
        buttonIcon="outline_clipboard_task"
        hideButton={someConclusionExist}
        onClick={() => setIsEditing(true)}
      />
    );

  const renderAdvertisement = () =>
    !someConclusionExist &&
    !isEditing && (
      <InvestigationAdvertisement
        title={t("Conclusion.no_conclusions_loaded")}
        description={t(
          "Conclusion.no_conclusions_loaded_description"
        )}
      />
    );

  const renderSectionTitle = () =>
    justView && (
      <Separator
        text={t("Conclusion.conclusions")}
        icon="outline_clipboard_task"
        iconButton={handleSelectedSection ? "outline_edit" : undefined}
        onClick={handleSelectedSection ? () => handleSelectedSection(enums.sections.conclusion) : undefined}
        sx={{ marginBottom: 1.5 }}
      />
    );

  const renderConclusion = () =>
    someConclusionExist &&
    !isEditing && (
      <Stack spacing={2}>
        {conclusion && (
          <SingleConclusion
            conclusion={conclusion}
            setIsEditing={setIsEditing}
            handleSaveConclusionDetails={handleSaveConclusionDetails}
            justView={justView}
          />
        )}
        {investigation.conclusion_at == null && !justView && (
          <Box display="flex" justifyContent="flex-end">
            <EGButton
              label={t("Conclusion.conclude_investigation_btn")}
              variant="contained"
              color="error"
              onClick={() => setShowConclude(true)}
              sx={{ width: "fit-content" }}
            />
          </Box>
        )}
      </Stack>
    );

  return (
    <Box className="dyTheme1" p={1}>
      {someConclusionExist && !justView && (
        <Conclude
          show={showConclude}
          onHide={() => setShowConclude(false)}
          onConfirm={handleConcludeInvestigation}
          defaultDate={conclusion?.conclusion_at}
          loading={isLoading}
        />
      )}
      <Box className="dyTheme1">
        {renderHeader()}
        {renderSectionTitle()}
        {renderAdvertisement()}
        {renderConclusion()}
        {isEditing && (
          <AddConclusion
            isLoading={isLoading}
            setIsLoading={setIsLoading}
            conclusion={conclusion}
            setIsEditing={setIsEditing}
            handleSaveConclusionDetails={handleSaveConclusionDetails}
          />
        )}
      </Box>
    </Box>
  );
};
